import { createLogger } from './logger';

const log = createLogger('ice');

/**
 * Google STUN fallback — mirrors the values previously hardcoded in peer.js.
 * Used when the Cloudflare TURN backend call fails or is not configured.
 */
export const FALLBACK_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * Fetch the current ICE server configuration from the backend.
 * The backend transparently mints short-lived Cloudflare TURN credentials.
 * Never receives permanent Cloudflare secrets — those stay server-side only.
 * On any failure (timeout, network, non-2xx, malformed body) returns the STUN
 * fallback so matchmaking is never blocked.
 */
export async function fetchIceServers(backendUrl) {
  if (!backendUrl) {
    log('no-backend-url-using-fallback');
    return { iceServers: FALLBACK_ICE_SERVERS, turnAvailable: false };
  }
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const res = await fetch(`${backendUrl}/api/webrtc/ice-servers`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) {
      log('fetch-non-ok', { status: res.status });
      return { iceServers: FALLBACK_ICE_SERVERS, turnAvailable: false };
    }
    const data = await res.json();
    const servers = Array.isArray(data?.iceServers) ? data.iceServers : null;
    if (!servers || servers.length === 0) {
      log('fetch-empty-using-fallback');
      return { iceServers: FALLBACK_ICE_SERVERS, turnAvailable: false };
    }
    // Diagnostic only — no usernames, no credentials.
    const kinds = new Set();
    servers.forEach((s) => {
      const urls = Array.isArray(s?.urls) ? s.urls : (s?.urls ? [s.urls] : []);
      urls.forEach((u) => {
        const scheme = String(u).split(':', 1)[0];
        if (scheme) kinds.add(scheme);
      });
    });
    log('fetch-ok', {
      serverCount: servers.length,
      schemes: Array.from(kinds),
      turnAvailable: !!data.turn_available,
      ttl: data.ttl || 0,
    });
    return {
      iceServers: servers,
      turnAvailable: !!data.turn_available,
      ttl: data.ttl || 0,
    };
  } catch (err) {
    log('fetch-error-using-fallback', { name: err?.name || 'Error' });
    return { iceServers: FALLBACK_ICE_SERVERS, turnAvailable: false };
  }
}
